import { Countries, Languages } from 'typings'
import getConstants from '../constants'
import { locales } from '../constants/locales'

const CONSTANTS = getConstants()

type Breakpoints = {
  sm?: number
  md?: number
  lg?: number
  xl?: number
}
export enum BusinessStatus {
  DISCONTINUED = 'DISCONTINUED',
  OPEN = 'OPEN',
  SUSPENDED = 'SUSPENDED',
  COMING_SOON = 'COMING_SOON',
}

export const spacingClassNames = (breakpoints: Breakpoints): string =>
  Object.entries(breakpoints)
    .map(([breakpoint, space]) => `fr-ec-mb-spacing-0${space}-${breakpoint}`)
    .join(' ')

export const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
)

export const simpleModeSearchPair = 'mode=simple'
export const detailsPagePathName = '/detail'

/** Should page be shown without header/footer */
export const hasSimpleModeFlag = (search: string) =>
  search.includes(simpleModeSearchPair)

export const isDetailsPage = (location: string) =>
  location.includes(detailsPagePathName)

const postalCodeRegex = new RegExp(CONSTANTS.POSTCODE_FORMAT)
export const isPostalCode = (term = '') =>
  CONSTANTS.POSTCODE_FORMAT && postalCodeRegex.test(term)

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export const getLanguageOptions = (intl: any, languages: any) => {
  const options = Object.entries(languages).map(([k, v]) => {
    const labelId = `text_locale_${v}`
    return {
      label: intl.formatMessage({ id: labelId }),
      value: v,
    }
  })
  return options
}

export const getCountryValue = (countryLanguageValue: string) =>
  countryLanguageValue.split('/')[0]

export const EUCountryLanguageOptions = {
  [`eu-${Countries.AT}/${Languages.EN}`]: {
    lid: `text_country_austria_english`,
    label: `🇦🇹 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`${Countries.BE}/${Languages.NL}`]: {
    lid: `text_country_belgium_dutch`,
    label: `🇧🇪 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_nl`,
  },
  [`${Countries.BE}/${Languages.FR}`]: {
    lid: `text_country_belgium_french`,
    label: `🇧🇪 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_fr`,
  },
  [`${Countries.BE}/${Languages.EN}`]: {
    lid: `text_country_belgium_english`,
    label: `🇧🇪 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_en`,
  },
  [`eu-${Countries.BG}/${Languages.EN}`]: {
    lid: `text_country_bulgaria_english`,
    label: `🇧🇬 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.HR}/${Languages.EN}`]: {
    lid: `text_country_croatia_english`,
    label: `🇭🇷 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.CZ}/${Languages.EN}`]: {
    lid: `text_country_czech_republic_english`,
    label: `🇨🇿 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`${Countries.DK}/${Languages.EN}`]: {
    lid: `text_country_denmark_english`,
    label: `🇩🇰 #{PLACEHOLDER_COUNTRY} : DKK`,
  },
  [`${Countries.DE}/${Languages.DE}`]: {
    lid: `text_country_germany_german`,
    label: `🇩🇪 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_de`,
  },
  [`${Countries.DE}/${Languages.EN}`]: {
    lid: `text_country_germany_english`,
    label: `🇩🇪 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_en`,
  },
  [`${Countries.ES}/${Languages.ES}`]: {
    lid: `text_country_spain_spanish`,
    label: `🇪🇸 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_es`,
  },
  [`${Countries.ES}/${Languages.EN}`]: {
    lid: `text_country_spain_english`,
    label: `🇪🇸 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_en`,
  },
  [`eu-${Countries.EE}/${Languages.EN}`]: {
    lid: `text_country_estonia_english`,
    label: `🇪🇪 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.FI}/${Languages.EN}`]: {
    lid: `text_country_finland_english`,
    label: `🇫🇮 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`${Countries.FR}/${Languages.FR}`]: {
    lid: `text_country_france_french`,
    label: `🇫🇷 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_fr`,
  },
  [`${Countries.FR}/${Languages.EN}`]: {
    lid: `text_country_france_english`,
    label: `🇫🇷 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_en`,
  },
  [`eu-${Countries.GR}/${Languages.EN}`]: {
    lid: `text_country_greece_english`,
    label: `🇬🇷 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.HU}/${Languages.EN}`]: {
    lid: `text_country_hungary_english`,
    label: `🇭🇺 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.IE}/${Languages.EN}`]: {
    lid: `text_country_ireland_english`,
    label: `🇮🇪 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`${Countries.IT}/${Languages.IT}`]: {
    lid: `text_country_italy_italian`,
    label: `🇮🇹 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_it`,
  },
  [`${Countries.IT}/${Languages.EN}`]: {
    lid: `text_country_italy_english`,
    label: `🇮🇹 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_en`,
  },
  [`eu-${Countries.LV}/${Languages.EN}`]: {
    lid: `text_country_latvia_english`,
    label: `🇱🇻 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.LT}/${Languages.EN}`]: {
    lid: `text_country_lithuania_english`,
    label: `🇱🇹 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.LU}/${Languages.EN}`]: {
    lid: `text_country_luxembourg_english`,
    label: `🇱🇺 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`${Countries.NL}/${Languages.NL}`]: {
    lid: `text_country_netherlands_dutch`,
    label: `🇳🇱 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_nl`,
  },
  [`${Countries.NL}/${Languages.EN}`]: {
    lid: `text_country_netherlands_english`,
    label: `🇳🇱 #{PLACEHOLDER_COUNTRY} (#{PLACEHOLDER_LOCALE}): €`,
    locale: `text_locale_en`,
  },
  [`eu-${Countries.PL}/${Languages.EN}`]: {
    lid: `text_country_poland_english`,
    label: `🇵🇱 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.PT}/${Languages.EN}`]: {
    lid: `text_country_portugal_english`,
    label: `🇵🇹 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.RO}/${Languages.EN}`]: {
    lid: `text_country_romania_english`,
    label: `🇷🇴 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.SK}/${Languages.EN}`]: {
    lid: `text_country_slovakia_english`,
    label: `🇸🇰 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`eu-${Countries.SI}/${Languages.EN}`]: {
    lid: `text_country_slovenia_english`,
    label: `🇸🇮 #{PLACEHOLDER_COUNTRY}: €`,
  },
  [`${Countries.SE}/${Languages.EN}`]: {
    lid: `text_country_sweden_english`,
    label: `🇸🇪 #{PLACEHOLDER_COUNTRY}: kr`,
  },
  [`${Countries.UK}/${Languages.EN}`]: {
    lid: `text_country_united_kingdom_english`,
    label: `🇬🇧 #{PLACEHOLDER_COUNTRY}: £`,
  },
}

/*
 * checks if the current language is available in that country, if so returns it otherwise defaults to EN
 */
export const getCountryLanguage = (countryCode: string): string => {
  const locale = locales[countryCode as keyof typeof locales]
  const languages = locale.LANGUAGES
    ? Object.values(locale.LANGUAGES)
    : [locale.LANGUAGE_CODE]

  return languages.includes(CONSTANTS.LANGUAGE)
    ? CONSTANTS.LANGUAGE
    : Languages.EN.toString()
}

/*
 * checks if the provided EU country supports store locator
 * if true returns it
 * else returns the first entry in supported countries (UK as of now)
 */
export const getSupportedEUCountry = (country: string) => {
  const countryData = CONSTANTS.EU_COUNTRY_LIST.find(
    item => item.countryCode === country,
  )

  return countryData?.defaultTo || country
}
